import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import Faq from "../components/Faq";
import RecentCarousel from "../components/RecentCarousel";
import { getIdentityHeaders } from "../lib/identity";
import { API_BASE } from "../lib/api";

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

export default function Home() {
  // Design tokens (tweak here to fine-tune brand color)
  const brand = {
    purple: "#6D5EF9",
    purpleDark: "#5B4EE8",
    purpleTint: "#F3F0FF",
    purpleBorder: "#E7E2FF",
    neutralBorder: "#E5E7EB",
    neutralText: "#111827",
    gradient: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
  };

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 18px",
    borderRadius: 12,
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    textDecoration: "none",
    transition: "all 160ms ease-in-out",
    userSelect: "none",
  };

  const btnPrimary = {
    ...btnBase,
    background: brand.purple,
    color: "#fff",
    boxShadow: "0 6px 16px rgba(109,94,249,0.25)",
  };

  const btnPrimaryHover = { background: brand.purpleDark };

  const btnSecondary = {
    ...btnBase,
    background: brand.purpleTint,
    color: brand.purple,
    border: `1px solid ${brand.purpleBorder}`,
  };

  // Collapsible state – default collapsed so the carousel stays visible
  const [howOpen, setHowOpen] = React.useState(false);

  /* ---------------------- Production FAQ content ---------------------- */
  const FAQ_GROUPS = [
    {
      title: "Wallet & identity",
      items: [
        {
          q: "How do I sign in?",
          a: "Use Pi Browser and tap Sign in with Pi. We authenticate with the Pi Platform SDK; your private keys never leave your device.",
        },
        {
          q: "Is my wallet non-custodial?",
          a: "Yes. You control your Pi wallet. ArtiBytes only requests your public address and payment approvals when needed.",
        },
        {
          q: "Which environments are supported?",
          a: "Pi Browser on mobile is the primary experience. Public pages load anywhere, but Pi payments require Pi Browser.",
        },
      ],
    },
    {
      title: "Payments & credits (Pi)",
      items: [
        {
          q: "What currency do you accept?",
          a: "Pi. Image generations cost 1 credit each. Video animations cost 15 credits (a premium feature with higher generation costs).",
        },
        {
          q: "How do top-ups work?",
          a: "Choose number of credits, approve the Pi payment, and credits are added after on-chain confirmation. The Pi amount is calculated against the live market rate at the time of purchase. Your transaction hash is recorded in your receipts.",
        },
        {
          q: "Who pays network fees?",
          a: "Any network fees are shown in the Pi payment flow before you confirm.",
        },
        {
          q: "Refunds",
          a: "If an image generation or video animation fails due to a technical error, we automatically re-credit your account. On-chain transfers are final; refunds are issued as credits, not as Pi.",
        },
      ],
    },
    {
      title: "Generating images",
      items: [
        { q: "Do I have to pick a Style?", a: "No—Default works great. Styles add curated recipes to steer the look." },
        { q: "Supported aspect ratios", a: "1:1, 16:9, 9:16, 4:3, 3:4. If you plan to animate your image, 16:9 or 9:16 give the best results (video output uses these ratios)." },
        { q: "Edit & re-generate", a: "Open a result → Edit prompt & style → Generate again (1 credit)." },
        { q: "Usage limits", a: "To keep things fair and stable, there are daily generation limits per user. Heavy spikes may queue jobs briefly. We auto-scale, but short waits can occur during peaks." },
      ],
    },
    {
      title: "Animating images (video)",
      items: [
        {
          q: "What is animation?",
          a: "Turn any image you've generated into a short video with motion and sound, powered by Google Veo. Water ripples, creatures move, scenes come alive — with audio.",
        },
        {
          q: "How much does it cost?",
          a: "15 credits per animation. Each video is roughly 8 seconds long with audio.",
        },
        {
          q: "How do I animate an image?",
          a: "Open any image on the Result page or in your Gallery, then tap Animate. Your video is ready in about 30–120 seconds.",
        },
        {
          q: "What aspect ratio works best?",
          a: "Videos are generated in 16:9 (landscape) or 9:16 (portrait). If your source image is square or another ratio, it's mapped to the closest video orientation. For best framing, generate in 16:9 or 9:16 before animating.",
        },
        {
          q: "Can I download the video?",
          a: "Yes. Use the Download video button on the Result page or in the Gallery lightbox. Videos can be shared like any other file on your device.",
        },
        {
          q: "What if animation fails?",
          a: "If a video fails to generate due to a technical error, your 15 credits are automatically refunded to your balance.",
        },
      ],
    },
    {
      title: "Style Guides",
      items: [
        {
          q: "Current Styles",
          a: "Gloom Pop (bold, playful color), Flare Form (high-contrast, glossy accents), Glow Morphic (soft rounded volumes, glow accents).",
        },
        {
          q: "Do Styles change the model?",
          a: "Some are curated prompts/params; others are trained presets backed by model tuning.",
        },
      ],
    },
    {
      title: "Creator Program & royalties",
      items: [
        {
          q: "How do royalties work?",
          a: "When others generate or animate using your approved Style, a portion of each paid generation accrues to you in Pi.",
        },
        {
          q: "What do I submit?",
          a: "A ZIP with up to 20 consistent images you own, plus a name/description. You'll accept a commercial license so we can train and serve your Style.",
        },
        {
          q: "Review & publishing",
          a: "We review for consistency, safety, and rights. If approved, we train and publish; your dashboard starts tracking usage.",
        },
        {
          q: "Payouts",
          a: "Royalties display in Pi with references. Withdraw to your Pi wallet once you reach the minimum threshold.",
        },
      ],
    },
    {
      title: "Downloads & sharing",
      items: [
        { q: "How do I download images?", a: "Use Download on Result or Gallery—this triggers a direct file save to your device." },
        { q: "How do I download videos?", a: "Use the Download video button on Result or in the Gallery lightbox once your animation is ready." },
        { q: "Home carousel", a: "Toggle Allow on Home for an image in your Gallery to feature it publicly (you can toggle off anytime)." },
      ],
    },
    {
      title: "Ownership, rights & safety",
      items: [
        { q: "Who owns the images and videos I generate?", a: "You do—subject to our Content Policy and any third-party rights in your prompts/uploads." },
        { q: "Commercial use", a: "Allowed, provided you comply with local laws and IP rights. Sensitive subjects may require permission." },
        { q: "Is the content AI-generated?", a: "Yes. All images and videos are produced by AI. You're responsible for how you use them, including any disclosure required where you share AI content." },
        { q: "Content rules", a: "No illegal, hateful, or adult/NSFW content; no attempts to replicate private individuals; no infringements. This applies to images, videos, prompts, and uploads." },
      ],
    },
    {
      title: "Data & storage",
      items: [
        { q: "Where are images and videos stored?", a: "Off-chain in secure Google Cloud Storage. We may store content hashes for integrity checks." },
        { q: "Where is my data processed?", a: "Image generation and storage are in the EU (Belgium). Video animation is processed by Google Veo in the US (Iowa) and protected by Standard Contractual Clauses. See our Privacy Policy for full details." },
        { q: "What personal data do you keep?", a: "Wallet address (public), handle, prompts, images, videos, and Creator submissions/metadata. See our Privacy Policy." },
      ],
    },
    {
      title: "Support & troubleshooting",
      items: [
        { q: "Wallet not connecting?", a: "Open in Pi Browser, ensure you're signed in, and re-approve permissions. Clear site permissions if needed." },
        { q: "Payment stuck/pending?", a: "Check your wallet transactions. If confirmed but credits didn't appear, contact support with the tx hash." },
        { q: "Image not in Home carousel?", a: "Make sure Allow on Home is enabled for that item, then refresh." },
        { q: "Contact", a: "Email jaytayeldev@gmail.com for support, abuse/DMCA, or privacy questions." },
      ],
    },
    {
      title: "Legal",
      items: [
        {
          q: "Licenses",
          a: "By submitting to the Creator Program, you grant ArtiBytes a non-exclusive, worldwide, commercial license to train and serve your Style. Full terms in our Terms of Service, Creator Agreement, and Content Policy.",
        },
      ],
    },
  ];

  return (
    <Screen>
      <StyleTag />

      {/* Hero */}
      <section>
        <h1
          className="h1"
          style={{
            background: brand.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          ArtiBytes
        </h1>
        <p className="lead">Your Style, Your Economy</p>

        {/* CTA row - REORDERED */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
          <Link
            to="/profile"
            style={btnPrimary}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, btnPrimaryHover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, { background: brand.purple })}
          >
            Buy Credits
          </Link>

          <Link to="/create" style={btnSecondary}>
            Start Creating
          </Link>

          <Link to="/creator" style={btnSecondary}>
            Royalty Program
          </Link>
        </div>
      </section>
      <button
  onClick={async () => {
    try {
      const res = await fetch(api("/api/creator/payouts/request"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getIdentityHeaders(),
        },
        body: JSON.stringify({ amountPi: 1 }),
      });
      const data = await res.json();
      if (res.ok) {
        alert("1 Pi claimed! 🎉");
      } else {
        alert("Claim failed: " + (data.error || "Unknown error"));
      }
    } catch (e) {
      alert("Error: " + e.message);
    }
  }}
  style={btnSecondary}
>
  🎁 Claim 1 Pi
</button>

      {/* How it works – collapsible with animated hint */}
      <section className="how-wrap">
        <button
          className="how-trigger"
          aria-expanded={howOpen}
          aria-controls="how-content"
          onClick={() => setHowOpen((v) => !v)}
          type="button"
        >
          <span className="how-title">
            How it works
            {!howOpen && <span className="how-hint">👇 Click to learn</span>}
          </span>
          <svg
            className={`chev ${howOpen ? "open" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M6 9l6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {howOpen && (
          <div id="how-content" className="how-steps">
            <article className="how-card">
              <div className="how-badge">1</div>
              <div className="how-body">
                <h3>Describe your image</h3>
                <p>
                  Write a clear prompt. Add details like subject, lighting, mood, and camera/style hints.
                </p>
              </div>
            </article>

            <article className="how-card">
              <div className="how-badge">2</div>
              <div className="how-body">
                <h3>Pick a Style (optional)</h3>
                <p>
                  Use a curated Style for a consistent look—or stick with Default for a flexible baseline.
                </p>
              </div>
            </article>

            <article className="how-card">
              <div className="how-badge">3</div>
              <div className="how-body">
                <h3>Generate & save</h3>
                <p>
                  Spend 1 credit to generate. Save, share, or re-generate with tweaks until it's perfect.
                </p>
              </div>
            </article>

            <article className="how-card">
              <div className="how-badge">4</div>
              <div className="how-body">
                <h3>Start earning royalties</h3>
                <p>
                  Submit your style and earn royalties in Pi when others use it to generate images.
                </p>
              </div>
            </article>
          </div>
        )}
      </section>

      {/* Featured Creators – no duplicate heading */}
      <section style={{ marginTop: 32 }}>
        <RecentCarousel />
      </section>

      {/* FAQ */}
      <section style={{ marginTop: 48 }}>
        <Faq
          groups={FAQ_GROUPS}
          title="FAQs"
          subtitle=""
          defaultGroup={0}
          showSearch={true}
          titleGradientCss="linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)"
        />
      </section>
    </Screen>
  );
}

/* Local styles (collapsible + original roomy cards) */
function StyleTag() {
  return (
    <style>{`
.how-wrap{ margin-top: 28px; }

/* Trigger row */
.how-trigger{
  width:100%;
  display:flex; align-items:center; justify-content:space-between;
  padding:12px 14px;
  border:1px solid #E5E7EB; border-radius:14px;
  background:#fff; cursor:pointer;
  box-shadow:0 8px 18px rgba(17,24,39,.05);
  transition: all 0.2s ease;
}

.how-trigger:hover{
  border-color: #6D5EF9;
  box-shadow: 0 8px 24px rgba(109,94,249,0.12);
}

.how-title{
  font-weight:900; font-size:18px;
  color:#111827;
  display: flex;
  align-items: center;
  gap: 12px;
}

.how-hint{
  font-size: 14px;
  font-weight: 700;
  color: #6D5EF9;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

.chev{ transition: transform .18s ease; color:#111827; }
.chev.open{ transform: rotate(180deg); }

/* Cards (collapsible content) */
.how-steps{
  margin-top:12px;
  display:grid; gap:14px; grid-template-columns: 1fr;
}
@media (min-width: 860px){
  .how-steps{ grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1100px){
  .how-steps{ grid-template-columns: repeat(4, 1fr); }
}
.how-card{
  display:flex; gap:14px; align-items:flex-start;
  border:1px solid #E5E7EB; background:#fff;
  border-radius:20px; padding:18px;
  box-shadow: 0 12px 28px rgba(17,24,39,.06);
}
.how-badge{
  flex:0 0 auto;
  width:44px; height:44px; border-radius:14px;
  display:grid; place-items:center; font-weight:900; color:#fff;
  background: linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%);
  box-shadow: 0 10px 24px rgba(109,94,249,.25);
  font-size:16px;
}
.how-body h3{
  margin:0 0 6px; font-size:18px; line-height:1.25; font-weight:900; color:#111827;
}
.how-body p{
  margin:0; color:#4B5563; line-height:1.55; font-size:14.5px;
}

/* Mobile polish */
@media (max-width: 720px){
  .how-trigger{ padding:12px; }
  .how-title{ font-size:17px; }
  .how-hint{ font-size:13px; }
  .how-card{ padding:16px; border-radius:18px; }
  .how-badge{ width:40px; height:40px; font-size:15px; }
  .how-body h3{ font-size:17px; }
  .how-body p{ font-size:14px; }
}
    `}</style>
  );
}